import { useEffect, useRef, type ReactNode } from "react";
import { X } from "lucide-react";
import styles from "./DockTabs.module.css";

/**
 * The dock's tab strip (2026-07-15) — extracted from ArtifactCanvas so the
 * artifact canvas, the terminal, the browser and the files panel all share ONE
 * implementation. Every behaviour here was paid for once already; a second
 * copy-pasted strip would have to re-learn all of it:
 *
 *  - roving `tabIndex` + Left/Right with wrap (the panel toolbar keyboard contract)
 *  - the active tab scrolls itself into view — without it, a newly-opened tab
 *    can open off-screen in a scrolled strip and read as "nothing happened"
 *  - close reveals on hover/focus but is PERMANENT on touch, where there is no
 *    hover to reveal it
 *  - tabs shrink to a min-width floor and then the strip SCROLLS, rather than
 *    shrinking to slivers (the VS Code contract)
 *  - one row, always: a wrapping strip steals height from the document below
 *
 * Rendering nothing for an empty tab list is deliberate: an empty strip is a
 * stray bordered band, and an empty `role="toolbar"` says nothing.
 */

export interface DockTab {
	id: string;
	title: string;
	/** Leading glyph — lets you find a panel without reading every truncated
	 *  label. Supplied by the caller so this stays kind-agnostic. */
	icon?: ReactNode;
}

interface DockTabsProps {
	tabs: DockTab[];
	activeId: string | null;
	onActivate: (id: string) => void;
	onClose: (id: string) => void;
	/** Distinguishes this toolbar from any other on the same panel (the artifact
	 *  canvas also has a Source/Preview tablist — both must stay addressable). */
	label: string;
	/** Merged onto the strip. The caller owns placement concerns — e.g. the chat
	 *  canvas reserves a mobile gutter for the Shell's fixed hamburger, which is
	 *  not something a tab strip should know about. */
	className?: string;
}

export function DockTabs({ tabs, activeId, onActivate, onClose, label, className }: DockTabsProps) {
	const activeRef = useRef<HTMLDivElement | null>(null);

	// Keep the focused tab reachable when the strip has scrolled past it.
	useEffect(() => {
		activeRef.current?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
	}, [activeId]);

	// Left/Right activate and focus the adjacent panel.
	const onKeyDown = (e: React.KeyboardEvent, index: number) => {
		if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
		e.preventDefault();
		const next = e.key === "ArrowLeft" ? index - 1 : index + 1;
		const target = tabs[(next + tabs.length) % tabs.length];
		if (target) {
			onActivate(target.id);
			const bar = e.currentTarget.closest('[role="toolbar"]');
			(bar?.querySelectorAll<HTMLButtonElement>('button[aria-pressed]')[(next + tabs.length) % tabs.length])?.focus();
		}
	};

	if (tabs.length === 0) return null;

	return (
		<div
			className={`${styles.tabBar} ${className ?? ""}`}
			role="toolbar"
			aria-label={label}
		>
			{tabs.map((t, i) => {
				const isActive = t.id === activeId;
				return (
					<div
						key={t.id}
						role="presentation"
						ref={isActive ? activeRef : undefined}
						className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
					>
						<button
							type="button"
							aria-pressed={isActive}
							tabIndex={isActive ? 0 : -1}
							className={styles.tabMain}
							onClick={() => onActivate(t.id)}
							onKeyDown={(e) => onKeyDown(e, i)}
							title={t.title}
						>
							{t.icon}
							<span className={styles.tabLabel}>{t.title}</span>
						</button>
						<button
							type="button"
							className={styles.tabClose}
							onClick={() => onClose(t.id)}
							title="Close tab"
							aria-label={`Close ${t.title}`}
						>
							<X size={12} />
						</button>
					</div>
				);
			})}
		</div>
	);
}
